import { createModel } from '@rematch/core';
import type { RootModel } from '.';
import { Group } from '../types/groups.types';
import { Team } from '../types/teams.types';
import { UserTypes } from '../types/user.types';

// ─── View Data Shapes ───────────────────────────────────────

export interface L1Group {
    id: string;
    name: string;
    location?: string | null;
    tags: string[];
    teamCount: number;
    memberCount: number;
    teamIds: string[];
    leadersIds?: string[];
    leaders: { id: string; firstName?: string; lastName?: string; fullname?: string; email?: string }[];
    subscribersIds?: string[];
    settings: Group['settings'];
}

export interface L1Stats {
    groups: number;
    teams: number;
    pomUsers: number;
    unassigned: number;
}

export interface L2Team {
    id: string;
    name?: string;
    location?: string | null;
    tags: string[];
    memberCount: number;
    membersIds?: string[];
    leadersIds?: string[];
    leaders: { id: string; firstName?: string; lastName?: string; fullname?: string; email?: string }[];
    settings: Team['settings'];
    subscribersIds?: string[];
}

export interface L2Stats {
    teams: number;
    members: number;
    groupLeaders: number;
}

export interface L3TeamDetail {
    team: Team;
    members: UserTypes.Model[];
    leaders: { id: string; firstName: string; lastName: string; email?: string }[];
    parentGroup: { id: string; name: string } | null;
    pagination: {
        page: number;
        pageSize: number;
        total: number;
        totalPages: number;
    };
}

export interface SearchResults {
    people: any[];
    teams: any[];
    groups: any[];
}

// ─── Store State ────────────────────────────────────────────

export interface GroupsTeamsStoreProps {
    // L1 — Overview
    l1Groups: L1Group[];
    l1Stats: L1Stats;

    // L2 — Group detail
    l2Group: {
        id: string;
        name: string;
        location?: string | null;
        tags: string[];
        teamIds?: string[];
        settings: Group['settings'];
        subscribersIds?: string[];
        leadersIds?: string[];
        groupLeaders?: { id: string; fullname?: string; firstName?: string; lastName?: string; email?: string }[];
    } | null;
    l2Teams: L2Team[];
    l2Stats: L2Stats;

    // L3 — Team detail
    l3: L3TeamDetail | null;

    // Search
    searchResults: SearchResults;

    // Subscriptions — set of "<targetType>:<targetId>" strings the current user watches
    mySubscriptions: Set<string>;

    // Loading
    loading: {
        overview: boolean;
        groupDetail: boolean;
        teamDetail: boolean;
        search: boolean;
        saving: boolean;
    };
}

const initialState: GroupsTeamsStoreProps = {
    l1Groups: [],
    l1Stats: { groups: 0, teams: 0, pomUsers: 0, unassigned: 0 },
    l2Group: null,
    l2Teams: [],
    l2Stats: { teams: 0, members: 0, groupLeaders: 0 },
    l3: null,
    searchResults: { people: [], teams: [], groups: [] },
    mySubscriptions: new Set<string>(),
    loading: {
        overview: false,
        groupDetail: false,
        teamDetail: false,
        search: false,
        saving: false,
    },
};

export const groupsTeams = createModel<RootModel>()({
    state: initialState,
    reducers: {
        toggleLoading(state, prop: keyof GroupsTeamsStoreProps['loading'], value: boolean) {
            return { ...state, loading: { ...state.loading, [prop]: value } };
        },

        // L1
        setOverview(state, payload: { groups: L1Group[]; stats: L1Stats }) {
            return { ...state, l1Groups: payload.groups, l1Stats: payload.stats };
        },

        // L2
        setGroupDetail(
            state,
            payload: {
                group: GroupsTeamsStoreProps['l2Group'];
                teams: L2Team[];
                stats: L2Stats;
            }
        ) {
            return {
                ...state,
                l2Group: payload.group,
                l2Teams: payload.teams,
                l2Stats: payload.stats,
            };
        },

        // L3
        setTeamDetail(state, payload: L3TeamDetail | null) {
            return { ...state, l3: payload };
        },

        updateTeamMembers(
            state,
            payload: { members: UserTypes.Model[]; pagination: L3TeamDetail['pagination'] }
        ) {
            if (!state.l3) return state;
            return {
                ...state,
                l3: { ...state.l3, members: payload.members, pagination: payload.pagination },
            };
        },

        // Search
        setSearchResults(state, payload: SearchResults) {
            return { ...state, searchResults: payload };
        },

        clearSearch(state) {
            return { ...state, searchResults: { people: [], teams: [], groups: [] } };
        },

        // Subscriptions
        setMySubscriptions(state, payload: Set<string>) {
            return { ...state, mySubscriptions: payload };
        },

        addSubscription(state, key: string) {
            const next = new Set(state.mySubscriptions);
            next.add(key);
            return { ...state, mySubscriptions: next };
        },

        removeSubscription(state, key: string) {
            const next = new Set(state.mySubscriptions);
            next.delete(key);
            return { ...state, mySubscriptions: next };
        },
    },
});
